// Lightweight role-based permission helpers.
// The authoritative enforcement lives in the backend RPCs (via user_has_role);
// these helpers drive UI visibility/disablement so the interface matches reality.

const MANAGERIAL_ROLES = new Set(["owner", "admin", "manager"]);

/**
 * Returns true if the given role may perform an action restricted to `allowed` roles.
 * Owner/admin/manager always pass; every other role must be explicitly listed.
 */
export function hasRole(role: string | null | undefined, ...allowed: string[]): boolean {
  if (!role) return false;
  if (MANAGERIAL_ROLES.has(role)) return true;
  return allowed.includes(role);
}

/** True only for owner/admin — used for connector/carrier account administration. */
export const isAdmin = (role: string | null | undefined) =>
  role === "owner" || role === "admin";

/** Roles allowed to adjust/transfer/receive inventory and operate the warehouse. */
export const canOperateInventory = (role: string | null | undefined) =>
  hasRole(role, "operations", "warehouse");

/** Roles allowed to work sales orders (allocate, fulfil, cancel). */
export const canWorkSales = (role: string | null | undefined) =>
  hasRole(role, "operations", "warehouse", "sales");

/**
 * Roles allowed to create/edit/allocate/cancel sales orders and issue refunds
 * or exchanges — the "sales office" authority set. Deliberately excludes
 * warehouse, which may only fulfil/ship and process physical returns.
 */
export const canManageSalesOrders = (role: string | null | undefined) =>
  hasRole(role, "sales", "operations");

/** Roles allowed to receive and manage purchase orders. */
export const canWorkPurchasing = (role: string | null | undefined) =>
  hasRole(role, "purchasing", "operations", "warehouse");

/**
 * Roles allowed to create / edit draft / approve / cancel purchase orders.
 * Operations and Warehouse are deliberately excluded — they may only view and
 * receive (see `canReceivePurchaseOrders`). Owner/admin/manager inherit.
 */
export const canManagePurchaseOrders = (role: string | null | undefined) =>
  hasRole(role, "purchasing");

/**
 * Roles allowed to receive against a purchase order (and, for navigation,
 * reach the purchasing area). Owner/admin/manager inherit.
 */
export const canReceivePurchaseOrders = (role: string | null | undefined) =>
  hasRole(role, "purchasing", "operations", "warehouse");

/** Roles allowed to manage the product catalogue (owner/admin/manager). */
export const canManageProducts = (role: string | null | undefined) =>
  hasRole(role, "manager");

/** Roles allowed to create and manage channel listings (owner/admin/manager/sales). */
export const canManageListings = (role: string | null | undefined) =>
  hasRole(role, "manager", "sales");

/**
 * Roles allowed to create/edit/archive suppliers and manage supplier–product
 * links. Mirrors `user_has_domain(org_id, 'suppliers')` = owner/admin + manager/purchasing.
 */
export const canManageSuppliers = (role: string | null | undefined) =>
  hasRole(role, "purchasing");

/**
 * Roles allowed to create/edit/archive customers. Mirrors
 * `user_has_domain(org_id, 'customers')` = owner/admin + manager/sales.
 */
export const canManageCustomers = (role: string | null | undefined) =>
  hasRole(role, "sales");

/**
 * Roles allowed to READ the customer master (name/contact/history). Mirrors the
 * migration 0048 `customers_select` policy: the sales/customers domain plus
 * operations/warehouse (who need the customer name on pick/pack/ship flows).
 * Purchasing and readonly are deliberately excluded — they cannot read customer PII.
 */
export const canReadCustomers = (role: string | null | undefined) =>
  hasRole(role, "sales", "operations", "warehouse");